/**
 * Where every piece of a text card lands in its table: the one placement the cell
 * writer writes by (`CellChangeWriter`) and the preview composer draws by
 * (`PreviewPackage.compose`), so a card's added lines are previewed where they
 * will be written.
 *
 * CASES, per piece of the card
 *   - a paragraph the card names by bookmark -> in place: the cell paragraph that bookmark names,
 *                                               redlined toward the card's text
 *   - a paragraph the card adds              -> after the paragraph its `afterBookmark` names, and
 *                                               after every paragraph the card added there before
 *                                               it: the added paragraphs form one group per anchor,
 *                                               in the card's order (`cardInsertionGroupsOf`)
 *
 * WHY one placement
 *   The writer placed an added line by the bookmark of the line it follows while the
 *   preview moved a card's paragraphs into the live table by their own bookmarks; an
 *   added paragraph has none, so it stayed in the paragraph body and was drawn above
 *   the table (page finding #10, 2026-10-16: "Name: Morgan Chen" and "Title: Director"
 *   over the signature table instead of after "By:" in its cell).
 */
import type { RedlineTextInsertion } from "@common/utils/redline/wire/redline-payload";

/**
 * The paragraphs a card adds after one of its paragraphs, in the card's order: the
 * first lands after the paragraph `afterBookmark` names, each later one after the
 * one before it. `insertedParagraphIds` holds, line for line with `insertedTexts`,
 * the id each new paragraph was proposed with, which the writer lays on the line it
 * adds (`RedlineTextInsertion.bookmark`).
 *
 * NULL
 *   An entry of `insertedParagraphIds` is null for a line proposed before new
 *   paragraphs carried an id: the line is added unbookmarked, as before.
 */
export interface CardInsertionGroup {
    afterBookmark: string;
    insertedTexts: string[];
    insertedParagraphIds: (string | null)[];
}

/**
 * The card's added paragraphs gathered by the paragraph they follow, each group in
 * the card's order, the groups in the order the card first names their paragraph.
 *
 * EXAMPLE
 *   [after _by "Name: Morgan Chen" as _tile_ember_quote, after _by "Title: Director" as _oak_lumen_drift]
 *   -> [{ afterBookmark: "_by", insertedTexts: ["Name: Morgan Chen", "Title: Director"],
 *         insertedParagraphIds: ["_tile_ember_quote", "_oak_lumen_drift"] }]
 *
 * RETURNS
 *   [] for a card that adds no paragraph.
 */
export function cardInsertionGroupsOf(cardInsertions: readonly RedlineTextInsertion[]): CardInsertionGroup[] {
    const insertionGroups: CardInsertionGroup[] = [];
    for (const { afterBookmark, textProposed, bookmark } of cardInsertions) {
        const existingGroup = insertionGroups.find((insertionGroup) => insertionGroup.afterBookmark === afterBookmark);
        /** NULL `bookmark` for a line proposed before new paragraphs carried an id. */
        const insertedParagraphId = bookmark ?? null;
        /**
         * CASES
         *   - a paragraph was already added after this bookmark -> this one joins its group, after it
         *   - otherwise                                         -> a new group
         */
        if (existingGroup !== undefined) {
            existingGroup.insertedTexts.push(textProposed);
            existingGroup.insertedParagraphIds.push(insertedParagraphId);
        } else {
            insertionGroups.push({ afterBookmark, insertedTexts: [textProposed], insertedParagraphIds: [insertedParagraphId] });
        }
    }
    return insertionGroups;
}
